import type { StoreProductOption, StoreProductVariant } from "@medusajs/types";
import { useForm } from "@tanstack/react-form";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { type ReactNode, useState } from "react";
import { z } from "zod";
import { pickBookCover } from "#/lib/assets";
import { isVariantOutOfStock, remainingStock } from "#/lib/inventory";
import type { BundleStock } from "#/lib/medusa-types/bundles";
import {
  getDisplayPrice,
  projectProductImages,
} from "#/lib/medusa-types/catalog";
import { formatMoney } from "#/lib/medusa-types/commerce";
import { ROUTES } from "#/lib/routes";
import { cn } from "#/lib/utils";
import {
  isBundleProduct,
  productQueries,
  useBundleStock,
  useProducts,
} from "#/queries/products";
import { regionQueries } from "#/queries/regions";
import { useCategories } from "#/queries/taxonomy";
import { useAddToCart, useLineForVariant } from "#/store/cart";
import { AddToCartControl } from "../components/AddToCartControl.js";
import { ProductCard } from "../components/ProductCard.js";
import { BookCover, Skeleton, Stars } from "../components/primitives.js";
import { Input, Textarea } from "../components/ui/input.js";
import ReactMarkdown from 'react-markdown'

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.length > 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function formatGrams(value: unknown): string | null {
  const n = toFiniteNumber(value);
  if (n == null) return null;
  if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 2)} kg`;
  return `${n} g`;
}

function formatDimensions(
  length: unknown,
  width: unknown,
  height: unknown,
): string | null {
  const l = toFiniteNumber(length);
  const w = toFiniteNumber(width);
  const h = toFiniteNumber(height);
  if (l == null && w == null && h == null) return null;
  const parts = [l, w, h].map((p) => (p == null ? "—" : `${p}`));
  return `${parts.join(" × ")} cm`;
}

const RELATED_LIMIT = 7;

// Map of option_id -> option_value_id representing the current axis selection.
type OptionSelection = Record<string, string>;

function variantToSelection(variant: StoreProductVariant): OptionSelection {
  const sel: OptionSelection = {};
  for (const ov of variant.options ?? []) {
    if (ov.option_id) sel[ov.option_id] = ov.id;
  }
  return sel;
}

function variantMatchesSelection(
  variant: StoreProductVariant,
  selection: OptionSelection,
): boolean {
  const ovs = variant.options ?? [];
  for (const [optionId, optionValueId] of Object.entries(selection)) {
    const match = ovs.find((ov) => ov.option_id === optionId);
    if (!match || match.id !== optionValueId) return false;
  }
  return true;
}

function findVariantForSelection(
  variants: readonly StoreProductVariant[],
  selection: OptionSelection,
): StoreProductVariant | undefined {
  return variants.find((v) => variantMatchesSelection(v, selection));
}

function isOptionValueAvailable(
  variants: readonly StoreProductVariant[],
  selection: OptionSelection,
  optionId: string,
  candidateValueId: string,
): boolean {
  const probe: OptionSelection = { ...selection, [optionId]: candidateValueId };
  return variants.some((v) => variantMatchesSelection(v, probe));
}

export const Route = createFileRoute("/p/$slug")({
  loader: async ({ context: { queryClient }, params }) => {
    const region = await queryClient.ensureQueryData(regionQueries.default());
    await queryClient.ensureQueryData(
      productQueries.detail(region?.id, params.slug),
    );
  },
  pendingComponent: ProductPageSkeleton,
  component: RouteComponent,
});

function RouteComponent() {
  const { slug } = Route.useParams();
  return <ProductPage slug={slug} />;
}

interface ProductPageProps {
  slug: string;
}

function ProductPage({ slug }: ProductPageProps) {
  const { data: region } = useSuspenseQuery(regionQueries.default());
  const regionId = region?.id;
  const { data: product } = useSuspenseQuery(
    productQueries.detail(regionId, slug),
  );
  const { data: categories } = useCategories();
  const addToCart = useAddToCart();

  const [thumbI, setThumbI] = useState(0);
  const variants: readonly StoreProductVariant[] = product?.variants ?? [];
  const options: readonly StoreProductOption[] = product?.options ?? [];
  const [selection, setSelection] = useState<OptionSelection>(() =>
    variants[0] ? variantToSelection(variants[0]) : {},
  );
  const qty = 1;

  const router = useRouter();

  const selectedVariant =
    findVariantForSelection(variants, selection) ?? variants[0];

  const isBundle = product ? isBundleProduct(product) : false;
  const bundleStockQuery = useBundleStock(
    isBundle ? selectedVariant?.id : undefined,
  );
  const bundleStock = bundleStockQuery.data;

  const selectedDisplay = getDisplayPrice(selectedVariant);
  // Bundles included: availability comes from the set variant's own inventory,
  // which the hourly Zoho reconcile mirrors from the composite's
  // available_for_sale. Components are not modeled as Medusa products, so the
  // component-derived bundle-stock value is not used for the buy decision.
  const selectedOOS = isVariantOutOfStock(selectedVariant);
  const stockOnHand = remainingStock(selectedVariant);
  const stepperMax = stockOnHand ?? 99;
  const clampedQty = Math.min(Math.max(qty, 1), Math.max(stepperMax, 1));



  if (!product) {
    return (
      <div className="py-12 text-center font-display text-lp-text">
        Product not found.{" "}
        <Link to={ROUTES.shop} className="underline">
          Browse the catalog
        </Link>
        .
      </div>
    );
  }

  const brandName = product.metadata.publisher ?? "Learning Plus";
  const images = projectProductImages(product);
  const cover =
    images[thumbI]?.url ?? images[0]?.url ?? pickBookCover(product.id);
  const thumbs = images.length > 0 ? images : null;

  const categoryId = product.metadata.categoryId;

  const category = (categories ?? []).find(
    (c) => c.id === product.metadata.categoryId,
  );

  const categoryNames = (product.categories ?? []).map( c => c.name)
  const categoryChips = Array.from(
    new Set(
      [
        category?.name,
        product.metadata.gradeLabel,
        product.metadata.format,
        product.metadata.series,
        brandName,
        ...categoryNames
      ].filter((s): s is string => Boolean(s)),
    ),
  );
  const tagChips = Array.from(
    new Set([brandName].filter((s): s is string => Boolean(s))),
  );
  
  const isItemAlreadyInCart = useLineForVariant(selectedVariant?.id)

  // useEffect(() => {
  //    setQty(1);
  //  }, [selectedVariant?.id]);

  return (
    <div data-screen-label="Product Detail" className="pb-24">
      <section className="relative pt-8 md:pt-12 pb-10">
        <div className="relative mx-auto w-full max-w-figma px-4 md:px-10">
          <Link
            to={ROUTES.shop || "/"}
            aria-label="Back to shop"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                            
              e.preventDefault();
              if (window.history.length > 1) {
                router.history.back();
              } else {
                router.navigate({ to: ROUTES.shop });
              }
            }}
            className="mb-6 inline-flex size-10 cursor-pointer items-center justify-center text-lp-text hover:text-lp-green-dk"
          >
            <svg
              aria-hidden="true"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
          </Link>

          <div className="relative grid grid-cols-1 lg:grid-cols-[440px_minmax(0,1fr)] gap-8 md:gap-12">
            <div
              aria-hidden
              className="pointer-events-none absolute bg-lp-green-soft hidden lg:block"
              style={{
                top: 0,
                bottom: 24,
                left: "calc(440px - 60px)",
                right: "calc(-1 * (50vw - 50%))",
                borderTopLeftRadius: 160,
                borderBottomLeftRadius: 160,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              }}
            />

            <div className="relative z-10 flex flex-col items-center pt-3">
              <BookCover
                src={cover}
                w={380}
                h={540}
                alt={product.title}
                fit="contain"
                style={{
                  width: "100%",
                  maxWidth: 380,
                  height: "auto",
                }}
              />
              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  aria-label="Previous thumbnail"
                  onClick={() => setThumbI((i) => Math.max(0, i - 1))}
                  className="flex size-9 cursor-pointer items-center justify-center rounded-full border-2 border-lp-green-mid bg-white text-lp-green-mid hover:bg-lp-green-soft"
                >
                  <svg
                    aria-hidden="true"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                {(thumbs ?? [{ id: "x", url: cover, alt: "" }]).map((t, i) => (
                  <button
                    type="button"
                    key={t.id}
                    aria-label={`Thumbnail ${i + 1}`}
                    onClick={() => setThumbI(i)}
                    className={`h-[70px] w-auto cursor-pointer overflow-hidden rounded-md border-2 bg-lp-grey/60 p-0 ${i === thumbI ? "border-lp-green-mid" : "border-transparent"}`}
                  >
                    <img
                      src={t.url}
                      alt={t.alt}
                      height={70}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.src = pickBookCover(product.id);
                      }}
                      className="h-full w-auto object-contain"
                    />
                  </button>
                ))}
                <button
                  type="button"
                  aria-label="Next thumbnail"
                  onClick={() =>
                    setThumbI((i) => Math.min((thumbs?.length ?? 1) - 1, i + 1))
                  }
                  className="flex size-9 cursor-pointer items-center justify-center rounded-full bg-lp-green text-white hover:bg-lp-green-mid"
                >
                  <svg
                    aria-hidden="true"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>

            <div
              className="relative z-10 pt-4 lg:pt-16 px-0 md:px-12  md:mx-auto lg:mx-0"
              style={{ maxWidth: 560 }}
            >
              <div className="mb-1 font-display text-sm md:text-base text-lp-text">
                {brandName}
              </div>
              <h1 className="m-0 mb-4 whitespace-pre-line font-bold font-display text-2xl md:text-[28px] text-lp-text leading-[1.15]">
                {product.title}
              </h1>
              <div className="mb-5 font-medium font-mono text-lp-text-dk text-xl">
                {formatMoney(
                  selectedDisplay?.amount ?? product.metadata.priceAmount,
                  selectedDisplay?.currencyCode ?? product.metadata.currency,
                )}
              </div>
              {(selectedDisplay?.compareAt ??
                product.metadata.compareAtAmount) != null && (
                <div className="-mt-3 mb-5 font-mono text-base text-lp-text-lt line-through">
                  {formatMoney(
                    selectedDisplay?.compareAt ??
                      product.metadata.compareAtAmount ??
                      0,
                    selectedDisplay?.currencyCode ?? product.metadata.currency,
                  )}
                </div>
              )}

              {options.length > 0 &&
                variants.length > 1 &&
                options.map((option) => {
                  const values = option.values ?? [];
                  if (values.length === 0) return null;
                  const selectedValueId = selection[option.id];
                  return (
                    <fieldset key={option.id} className="mb-5 border-0 p-0">
                      <legend className="mb-2 font-medium font-mono text-lp-text-dk text-xl">
                        {option.title}
                      </legend>
                      <div className="flex flex-wrap gap-2">
                        {values.map((ov) => {
                          const checked = ov.id === selectedValueId;
                          const available = isOptionValueAvailable(
                            variants,
                            selection,
                            option.id,
                            ov.id,
                          );
                          return (
                            <label
                              key={ov.id}
                              className={cn(
                                "cursor-pointer rounded-full border-2 px-4 py-2 font-display text-sm",
                                checked
                                  ? "border-lp-green-mid bg-lp-green-soft text-lp-green-dk"
                                  : "border-lp-grey bg-white text-lp-text-dk hover:border-lp-green-mid",
                                !available && "cursor-not-allowed opacity-60",
                              )}
                            >
                              <input
                                type="radio"
                                name={`option-${option.id}`}
                                value={ov.id}
                                checked={checked}
                                disabled={!available}
                                onChange={() =>
                                  setSelection((prev) => ({
                                    ...prev,
                                    [option.id]: ov.id,
                                  }))
                                }
                                className="sr-only"
                              />
                              {ov.value}
                            </label>
                          );
                        })}
                      </div>
                    </fieldset>
                  );
                })}

              <div
                className="mb-4 font-display text-sm"
                data-testid="pdp-inventory"
              >
                {selectedOOS ? (
                  <span className="font-semibold text-destructive">
                    Out of stock
                  </span>
                ) : stockOnHand != null ? (
                  <span className="font-semibold text-lp-green-dk">
                    In stock ({stockOnHand})
                  </span>
                ) : (
                  <span className="font-semibold text-lp-green-dk">
                    In stock
                  </span>
                )}
              </div>

              <div className="mb-4">
                <AddToCartControl
                  key={selectedVariant?.id ?? "no-variant"}
                  variant={selectedVariant}
                  productTitle={product.title}
                  size="xl"
                  className="border-lp-green-mid bg-white text-lp-green-mid shadow-sm"
                  renderAdd={({ add, disabled, outOfStock }) => (
                    <button
                      type="button"
                      onClick={() => add(clampedQty)}
                      disabled={disabled}
                      className="h-[60px] w-full md:max-w-[460px] cursor-pointer rounded-full border-2 border-lp-green-mid bg-white px-9 font-display font-semibold text-lg text-lp-green-mid hover:bg-lp-green-soft disabled:opacity-50 disabled:cursor-not-allowed block"
                    >
                      {outOfStock ? "Out of Stock" : "Add To Cart"}
                    </button>
                  )}
                />
              </div>
              <Link
                to={ROUTES.checkout}
                onClick={(e) => {
                  if (selectedOOS || !selectedVariant) {
                    e.preventDefault();
                    return;
                  }
                  if (selectedVariant && !isItemAlreadyInCart){
                    addToCart.mutate({
                      variant_id: selectedVariant.id,
                      quantity: clampedQty,
                    });
                  }
                }}
                aria-disabled={selectedOOS}
                className={cn(
                  "mb-7 flex h-[60px] w-full md:max-w-[460px] cursor-pointer items-center justify-center rounded-full border-0 bg-lp-green-mid font-display font-semibold text-lg text-white hover:bg-lp-green-dk",
                  selectedOOS && "pointer-events-none opacity-50",
                )}
              >
                Buy Now
              </Link>

              {isBundle && bundleStock && (
                <BundleContents stock={bundleStock} />
              )}

              <div className="md:max-w-[460px] pb-10 font-display text-lp-text-dk text-sm leading-relaxed">
                <div className="mb-2">
                  <span className="font-semibold">Categories: </span>
                  {categoryChips.map((c, i) => (
                    <span key={c}>
                      <span className="hover:underline">{c}</span>
                      {i < categoryChips.length - 1 && <span> , </span>}
                    </span>
                  ))}
                </div>
                <div className="mb-1">
                  <span className="font-semibold">Tags: </span>
                  {tagChips.map((c, i) => (
                    <span key={c}>
                      <span className="hover:underline">{c}</span>
                      {i < tagChips.length - 1 && <span> , </span>}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-figma-tight px-4 md:px-10 pt-8">
        
        <Tabs
          items={[
            {
              id: "desc",
              label: "Description",
              content: (
                <div className="space-y-3">
                  {
                    product.description ? (
                      product.description.split('\n').map((line, index) => (
                        <p
                          key={index}
                          className="m-0 font-display text-base text-lp-text  my-3"
                        >
                          <ReactMarkdown>
                            {line}
                          </ReactMarkdown>
                        </p>
                      ))
                    ) : (
                      <p className="m-0 font-display text-base text-lp-text px-4 py-2">
                        <ReactMarkdown>
                          {"Curated for homeschool families across the Philippines."}
                        </ReactMarkdown>
                      </p>
                    )
                  }
                </div>
              ),
            },
            {
              id: "info",
              label: "Additional Information",
              content: (
                <dl className="grid grid-cols-[160px_1fr] gap-y-3 font-display text-base text-lp-text leading-relaxed">
                  <dt className="font-semibold">Publisher</dt>
                  <dd>{brandName}</dd>
                  <dt className="font-semibold">Format</dt>
                  <dd>{product.metadata.format ?? "Paperback"}</dd>
                  <dt className="font-semibold">ISBN</dt>
                  <dd>{product.metadata.isbn ?? "—"}</dd>
                  <dt className="font-semibold">Grade</dt>
                  <dd>{product.metadata.gradeLabel ?? "All ages"}</dd>
                  <dt className="font-semibold">Weight</dt>
                  <dd>{formatGrams(product.weight) ?? "—"}</dd>
                  <dt className="font-semibold">Dimensions</dt>
                  <dd>
                    {formatDimensions(
                      product.length,
                      product.width,
                      product.height,
                    ) ?? "—"}
                  </dd>
                </dl>
              ),
            },
            {
              id: "rev",
              label: "Reviews",
              content: (
                <ReviewsPanel ratingCount={product.metadata.ratingCount} />
              ),
            },
          ]}
        />
      </section>

      {categoryId && (
        <RelatedProducts
          categoryId={categoryId}
          excludeId={product.id}
          brandName={brandName}
        />
      )}
    </div>
  );
}

function BundleContents({ stock }: { stock: BundleStock }) {
  return (
    <section
      data-testid="pdp-bundle-contents"
      className="mb-7 md:max-w-[460px] rounded-3xl border-2 border-lp-grey/60 bg-white p-5"
    >
      <h2 className="mb-3 font-bold font-display text-lp-text-dk text-lg">
        What's included
      </h2>
      <ul className="m-0 list-none space-y-2 p-0 font-display text-lp-text text-sm leading-relaxed">
        {stock.components.map((c, i) => (
          <li
            key={`${c.sku}-${c.variant_id ?? "unlinked"}-${i}`}
            className="flex items-baseline justify-between gap-3"
          >
            <span>
              <span className="font-semibold">{c.product_title || c.sku}</span>
              <span className="text-lp-text-lt"> × {c.mapped_qty}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

interface RelatedProductsProps {
  categoryId: string;
  excludeId: string;
  brandName: string;
}

function RelatedProducts({
  categoryId,
  excludeId,
  brandName,
}: RelatedProductsProps) {
  const { data } = useProducts({ categoryId, limit: RELATED_LIMIT });
  const [relI, setRelI] = useState(0);
  if (data === undefined) return null;
  const relatedItems = data.items.filter((p) => p.id !== excludeId).slice(0, 8);
  if (relatedItems.length === 0) return null;
  const relPageSize = 4;
  const relatedVisible = relatedItems.slice(relI, relI + relPageSize);
  const canPrev = relI > 0;
  const canNext = relI < Math.max(0, relatedItems.length - relPageSize);

  return (
    <section className="relative mx-auto mt-20 w-full max-w-figma px-6 md:px-10">
      <h3 className="mb-10 text-center font-bold font-display text-2xl md:text-3xl text-lp-green-dk">
        Related Products
      </h3>
      <div className="relative">
        <button
          type="button"
          aria-label="Previous"
          onClick={() => setRelI((i) => Math.max(0, i - 1))}
          disabled={!canPrev}
          className="-translate-y-1/2 absolute top-1/2 left-[-12px] lg:left-[-44px] z-10 hidden md:flex size-10 md:size-12 cursor-pointer items-center justify-center rounded-full border-2 border-lp-green bg-white text-lp-green hover:bg-lp-green-soft disabled:opacity-40"
        >
          <svg
            aria-hidden="true"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-8 px-2 md:px-0">
          {relatedVisible.map((p) => (
            <ProductCard key={p.id} product={p} brandName={brandName} />
          ))}
          {relatedVisible.length < relPageSize &&
            Array.from({
              length: relPageSize - relatedVisible.length,
            }).map((_, i) => (
              <div key={`pad-${i}`} className="hidden md:block" />
            ))}
        </div>
        <button
          type="button"
          aria-label="Next"
          onClick={() =>
            setRelI((i) =>
              Math.min(Math.max(0, relatedItems.length - relPageSize), i + 1),
            )
          }
          disabled={!canNext}
          className="-translate-y-1/2 absolute top-1/2 right-[-12px] lg:right-[-44px] z-10 hidden md:flex size-10 md:size-12 cursor-pointer items-center justify-center rounded-full border-2 border-lp-green bg-white text-lp-green hover:bg-lp-green-soft disabled:opacity-40"
        >
          <svg
            aria-hidden="true"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    </section>
  );
}

interface TabItem {
  id: string;
  label: string;
  content: ReactNode;
}

function Tabs({ items }: { items: readonly TabItem[] }) {
  const [active, setActive] = useState(items[0]?.id);
  const current = items.find((t) => t.id === active) ?? items[0];
  return (
    <div>
      <div className="hidden lg:block">
        <div className="h-px bg-lp-grey/70" />
        <div
          className="relative grid"
          style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }}
        >
          {items.map((t) => {
            const isActive = t.id === active;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActive(t.id)}
                className={`relative h-20 cursor-pointer border-0 bg-transparent font-display text-2xl ${
                  isActive
                    ? "font-bold text-lp-green-mid"
                    : "font-semibold text-lp-text"
                }`}
              >
                <span className="relative inline-block">
                  {t.label}
                  {isActive && (
                    <span className="absolute -bottom-[26px] left-0 right-0 h-[3px] bg-lp-green-mid" />
                  )}
                </span>
              </button>
            );
          })}
        </div>
        <div className="mb-10 h-px bg-lp-grey/70" />
        <div className="min-h-[160px] max-w-3xl">{current?.content}</div>
      </div>
      <div className="lg:hidden">
        <Accordion items={items} />
      </div>
    </div>
  );
}

function Accordion({ items }: { items: ReadonlyArray<TabItem> }) {
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <div className="divide-y divide-border">
      {items.map((it) => {
        const isOpen = openId === it.id;
        return (
          <div key={it.id}>
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => setOpenId(isOpen ? null : it.id)}
              className="w-full flex items-center justify-between py-5 bg-transparent border-0 cursor-pointer text-left"
            >
              <span className="font-semibold text-lg text-foreground">
                {it.label}
              </span>
              <ChevronDown
                size={20}
                className={cn(
                  "text-primary transition-transform shrink-0 ml-4",
                  isOpen && "rotate-180",
                )}
              />
            </button>
            {isOpen && (
              <div className="pb-5 text-base text-foreground/80 leading-relaxed">
                {it.content}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const reviewFormSchema = z.object({
  rating: z.number().int().min(1, "Pick a rating").max(5, "Pick a rating"),
  name: z.string().min(1, "Required"),
  email: z.string().email("Enter a valid email"),
  body: z.string().min(10, "Tell us a bit more (10+ characters)"),
});

type ReviewFormValues = z.infer<typeof reviewFormSchema>;

const EMPTY_REVIEW = {
  rating: 5,
  name: "",
  email: "",
  body: "",
} satisfies ReviewFormValues;

function ReviewsPanel({ ratingCount }: { ratingCount: number }) {
  const [showForm, setShowForm] = useState(false);
  const [hovered, setHovered] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm({
    defaultValues: EMPTY_REVIEW,
    validators: {
      onChange: reviewFormSchema,
      onSubmit: reviewFormSchema,
    },
    onSubmit: () => {
      setSubmitted(true);
    },
  });

  return (
    <div className="font-display text-base text-lp-text leading-relaxed">
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Stars rating={5} />
          <span className="font-semibold">5.0</span>
          <span className="text-lp-text-lt">· {ratingCount} reviews</span>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="h-11 cursor-pointer rounded-full border-2 border-lp-green-mid bg-white px-6 font-display font-semibold text-base text-lp-green-mid hover:bg-lp-green-soft"
        >
          {showForm ? "Cancel" : "Leave a Review"}
        </button>
      </div>

      {showForm && !submitted && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="mt-4 max-w-2xl rounded-3xl border-2 border-lp-grey/60 bg-white p-6 md:p-8"
        >
          <div className="mb-4 font-display font-semibold text-xl text-lp-green-dk">
            Share your thoughts
          </div>

          <form.Field name="rating">
            {(field) => (
              <div className="mb-5">
                <div className="mb-2 font-display font-medium text-lp-text-dk">
                  Your rating*
                </div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onMouseEnter={() => setHovered(n)}
                      onMouseLeave={() => setHovered(0)}
                      onClick={() => field.handleChange(n)}
                      onBlur={field.handleBlur}
                      aria-label={`Rate ${n} of 5`}
                      className="cursor-pointer border-0 bg-transparent p-1 leading-none"
                    >
                      <svg
                        aria-hidden="true"
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill={
                          (hovered || field.state.value) >= n
                            ? "#f5b400"
                            : "none"
                        }
                        stroke={
                          (hovered || field.state.value) >= n
                            ? "#f5b400"
                            : "#cdd2c8"
                        }
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polygon points="12 2 15 9 22 9.3 16.5 14 18.3 21 12 17 5.7 21 7.5 14 2 9.3 9 9" />
                      </svg>
                    </button>
                  ))}
                </div>
                {field.state.meta.isTouched &&
                  field.state.meta.errors.length > 0 && (
                    <p
                      id={`${field.name}-error`}
                      role="alert"
                      className="mt-2 font-display text-destructive text-xs"
                    >
                      {field.state.meta.errors
                        .map((e) =>
                          typeof e === "string" ? e : (e?.message ?? "Invalid"),
                        )
                        .join(", ")}
                    </p>
                  )}
              </div>
            )}
          </form.Field>

          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <form.Field name="name">
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched &&
                  field.state.meta.errors.length > 0;
                return (
                  <label className="block" htmlFor={field.name}>
                    <span className="mb-2 block font-display font-medium text-lp-text-dk">
                      Name*
                    </span>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="text"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      aria-invalid={isInvalid}
                      aria-describedby={
                        isInvalid ? `${field.name}-error` : undefined
                      }
                      className="h-12"
                    />
                    {isInvalid && (
                      <p
                        id={`${field.name}-error`}
                        role="alert"
                        className="mt-1 font-display text-destructive text-xs"
                      >
                        {field.state.meta.errors
                          .map((e) =>
                            typeof e === "string"
                              ? e
                              : (e?.message ?? "Invalid"),
                          )
                          .join(", ")}
                      </p>
                    )}
                  </label>
                );
              }}
            </form.Field>
            <form.Field name="email">
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched &&
                  field.state.meta.errors.length > 0;
                return (
                  <label className="block" htmlFor={field.name}>
                    <span className="mb-2 block font-display font-medium text-lp-text-dk">
                      Email*
                    </span>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="email"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      aria-invalid={isInvalid}
                      aria-describedby={
                        isInvalid ? `${field.name}-error` : undefined
                      }
                      className="h-12"
                    />
                    {isInvalid && (
                      <p
                        id={`${field.name}-error`}
                        role="alert"
                        className="mt-1 font-display text-destructive text-xs"
                      >
                        {field.state.meta.errors
                          .map((e) =>
                            typeof e === "string"
                              ? e
                              : (e?.message ?? "Invalid"),
                          )
                          .join(", ")}
                      </p>
                    )}
                  </label>
                );
              }}
            </form.Field>
          </div>

          <form.Field name="body">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched &&
                field.state.meta.errors.length > 0;
              return (
                <label className="mb-5 block" htmlFor={field.name}>
                  <span className="mb-2 block font-display font-medium text-lp-text-dk">
                    Your review*
                  </span>
                  <Textarea
                    id={field.name}
                    name={field.name}
                    rows={5}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    aria-invalid={isInvalid}
                    aria-describedby={
                      isInvalid ? `${field.name}-error` : undefined
                    }
                    className="rounded-3xl py-4"
                  />
                  {isInvalid && (
                    <p
                      id={`${field.name}-error`}
                      role="alert"
                      className="mt-1 font-display text-destructive text-xs"
                    >
                      {field.state.meta.errors
                        .map((e) =>
                          typeof e === "string" ? e : (e?.message ?? "Invalid"),
                        )
                        .join(", ")}
                    </p>
                  )}
                </label>
              );
            }}
          </form.Field>

          <div className="flex justify-end">
            <form.Subscribe
              selector={(state) => ({
                canSubmit: state.canSubmit,
                isSubmitting: state.isSubmitting,
              })}
            >
              {({ canSubmit, isSubmitting }) => (
                <button
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  className="h-12 cursor-pointer rounded-full border-0 bg-lp-green-mid px-8 font-display font-semibold text-base text-white hover:bg-lp-green-dk disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Submitting…" : "Submit Review"}
                </button>
              )}
            </form.Subscribe>
          </div>
        </form>
      )}

      {submitted && (
        <div className="mt-4 max-w-2xl rounded-3xl bg-lp-green-soft p-6 font-display text-lp-green-dk">
          Thanks for sharing! Your review has been submitted for moderation.
        </div>
      )}

      {!showForm && !submitted && (
        <p className="m-0">Reviews from verified buyers will appear here.</p>
      )}
    </div>
  );
}

function ProductPageSkeleton() {
  return (
    <div
      data-screen-label="Product Detail"
      className="pb-24"
      aria-label="Loading product"
      role="status"
    >
      <section className="relative pt-8 md:pt-12 pb-10">
        <div className="relative mx-auto w-full max-w-figma px-4 md:px-10">
          <Skeleton className="mb-6 size-10 rounded-full" />
          <div className="relative grid grid-cols-1 lg:grid-cols-[440px_minmax(0,1fr)] gap-8 md:gap-12">
            <div
              aria-hidden
              className="pointer-events-none absolute hidden bg-lp-green-soft/40 lg:block"
              style={{
                top: 0,
                bottom: 24,
                left: "calc(440px - 60px)",
                right: "calc(-1 * (50vw - 50%))",
                borderTopLeftRadius: 160,
                borderBottomLeftRadius: 160,
              }}
            />
            <div className="relative z-10 flex flex-col items-center pt-3">
              <Skeleton
                className="rounded-md w-full"
                style={{
                  maxWidth: 380,
                  aspectRatio: "380 / 540",
                }}
              />
              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                <Skeleton className="size-9 rounded-full" />
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="size-[70px] rounded-md" />
                ))}
                <Skeleton className="size-9 rounded-full" />
              </div>
            </div>
            <div
              className="relative z-10 pt-4 lg:pt-16 px-0 md:px-12 md:mx-auto lg:mx-0"
              style={{ maxWidth: 560 }}
            >
              <Skeleton className="mb-2 h-4 w-32" />
              <Skeleton className="mb-3 h-7 w-3/4" />
              <Skeleton className="mb-2 h-7 w-1/2" />
              <Skeleton className="mb-6 h-6 w-24" />
              <Skeleton className="mb-3 h-6 w-28" />
              <Skeleton className="mb-4 h-[60px] w-full md:max-w-[460px] rounded-full" />
              <Skeleton className="mb-7 h-[60px] w-full md:max-w-[460px] rounded-full" />
              <div className="md:max-w-[460px] space-y-2 pb-10">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-1/3" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
